import { useTranslation } from "react-i18next";

import { useGetReviewProviderScores } from "@krea/customer-features/store/cms-site-settings/hooks";
import { ReviewProviderEnum } from "@krea/customer-features/types/reviewProvider";
import { getCountryCode } from "@krea/customer-features/utils";
import { ReviewProviderDisplay } from "@krea/shared/design-system/review-provider";
import { CountryCode } from "@krea/shared/types/common";

import { GOOGLE_FALLBACK_LINKS, TRUSTPILOT_FALLBACK_LINK } from "./constants";

const countryCode = getCountryCode();

const PROVIDER_MAP: Record<string, "trustpilot" | "google_reviews"> = {
  [ReviewProviderEnum.TRUSTPILOT]: "trustpilot",
  [ReviewProviderEnum.GOOGLE_REVIEWS]: "google_reviews",
};

interface ICustomerReviewProviderBadgeProps {
  provider: ReviewProviderEnum;
  className?: string;
  isSimpleVersion?: boolean;
}

const CustomerReviewProviderBadge = ({
  provider,
  className,
  isSimpleVersion,
}: ICustomerReviewProviderBadgeProps) => {
  const { t } = useTranslation();
  const data = useGetReviewProviderScores(provider);

  if (!data) {
    return null;
  }

  const linkToReviews =
    data.linkToProviderReviews ||
    (provider === ReviewProviderEnum.GOOGLE_REVIEWS
      ? (GOOGLE_FALLBACK_LINKS[countryCode] ?? "")
      : TRUSTPILOT_FALLBACK_LINK);

  const badgeData = { ...data, linkToProviderReviews: linkToReviews };
  const isGoogle = provider === ReviewProviderEnum.GOOGLE_REVIEWS;
  const displayProvider = PROVIDER_MAP[provider] ?? "trustpilot";

  return (
    <ReviewProviderDisplay
      data={badgeData}
      provider={displayProvider}
      mode="badge"
      className={className}
      gradeLabel={
        isGoogle ? undefined : t("reviewProvider.trustpilotWidget.grade")
      }
      showReviewCount={!isSimpleVersion}
      reviewCountText={
        isGoogle
          ? t("reviewProvider.google.numberOfReviews", {
              numberOfReviews: data.numberOfReviews ?? "",
            })
          : `${data.numberOfReviews} ${t("reviewProvider.reviews")}`
      }
    />
  );
};

export default CustomerReviewProviderBadge;

const BADGE_PROVIDERS_PER_COUNTRY: Record<CountryCode, ReviewProviderEnum[]> = {
  [CountryCode.SE]: [
    ReviewProviderEnum.TRUSTPILOT,
    ReviewProviderEnum.GOOGLE_REVIEWS,
  ],
  [CountryCode.FI]: [ReviewProviderEnum.GOOGLE_REVIEWS],
};

interface IReviewProviderBadgeListProps {
  countryCode: CountryCode;
  isSimpleVersion?: boolean;
  className?: string;
}

export const ReviewProviderBadgeList = ({
  countryCode: country,
  isSimpleVersion,
  className,
}: IReviewProviderBadgeListProps) => {
  const providers = BADGE_PROVIDERS_PER_COUNTRY[country] ?? [];
  return (
    <>
      {providers.map((provider) => (
        <CustomerReviewProviderBadge
          key={provider}
          provider={provider}
          className={className}
          isSimpleVersion={isSimpleVersion}
        />
      ))}
    </>
  );
};
